import RiskMatrixCard from './RiskMatrixCard'
import type { RiskAcceptanceDTO, RiskItemDTO } from '@deepguide-ai/types'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ShowMoreClamp } from '@/components/ShowMoreClamp'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  AcceptanceCard,
  AcceptedHeaderPill,
  AcceptRiskDialog,
  AffectedAssetsSection,
  ExpiredAcceptanceBanner,
  LinkedTasksSection,
  LinkedTestsSection,
  RelatedVendorsSection,
  RiskActivitySection,
} from '@/pages/risk/RiskDetailSections'
import { Label } from '@deepguide-ai/ui/label'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import { RoleSelect } from '@/components/compliance/RoleSelect'
import { Button } from '@deepguide-ai/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@deepguide-ai/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@deepguide-ai/ui/select'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@deepguide-ai/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@deepguide-ai/ui/tabs'
import {
  AlertCircle,
  ArrowRight,
  CircleSlash,
  MoreHorizontal,
  ExternalLink,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react'
import { Streamdown } from 'streamdown'
import { formatDate } from '@/lib/format'
import {
  riskRatingBadgeClass,
  riskRatingSignal,
  statusBadgeClasses,
  type StatusColor,
} from '@/lib/status-colors'
import { trpc } from '@/lib/trpc'
import { StatusBadge } from './RiskBadges'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import {
  DetailPanelLayout,
  DetailPanelMain,
  DetailPanelTitle,
  DetailSection,
  DetailStatusGrid,
  DetailStatusItem,
  DETAIL_SECTION_TITLE_CLASS,
} from '@/components/compliance/detail-primitives'

const RISK_CATEGORY_OPTIONS = ['Access', 'Availability', 'Change Management', 'Compliance', 'Financial', 'Fraud', 'HR', 'Security', 'Vendor', 'Vulnerability Management', 'Policy exception', 'Other']

type RiskDetailPanelProps = {
  item: RiskDetailItem
  onClose: () => void
  onUpdate: () => void
  onMetadataSaved?: (item: RiskItemDTO) => void
  readOnly?: boolean
  acceptanceInAssessmentPanel?: boolean
  reviewingResidual?: boolean
  prioritizeResidualAssessment?: boolean
  viewStage?: string
  tab?: string
  onTabChange?: (tab: string) => void
  assessmentPanel?: ReactNode | ((description: ReactNode) => ReactNode)
  inProgressAssessment?: ReactNode
  closedActions?: ReactNode
  closedAssessment?: ReactNode | ((scoreOverview: ReactNode) => ReactNode)
  pendingAssessment?: ReactNode
  assessmentPreview?: ReactNode
  openAssessmentActions?: ReactNode
  assessmentHistory?: ReactNode
  menuActions?: ReactNode
}

type RiskDetailItem = {
  id: string
  displayId: string
  riskRating: string
  invalidatedAt?: string | Date | null
  invalidationReason?: string | null
  aiAssessmentRunning?: boolean
  canAssessRisk?: boolean
  canAssignRiskOwner?: boolean
  ownerLabel?: string | null
  ownerRoleName?: string | null
  riskType?: string
  createdAt?: string | Date | null
  canManageValidity?: boolean
  hasCompletedAssessment?: boolean
  hasConfirmedAssessment?: boolean
  riskScore?: number | null
  scoringVersion?: number
  status: string
  title: string
  riskStatement?: string | null
  mitigationPlan?: string | null
  ownerId?: string | null
  treatment: string
  residualRisk?: string | null
  residualScore?: number | null
  residualLikelihood?: string | null
  residualImpact?: string | null
  targetDate?: string | Date | null
  reviewDate?: string | Date | null
  category: string
  likelihood: string
  impact: string
  externalUrl?: string | null
  syncWarnings?: string[] | null
  provider?: string | null
  // #996: vulnerability CVE identity + remediation SLA (present on scanner-
  // sourced vulnerability risks; absent on manual/operational risks).
  cveIds?: string[]
  advisoryIds?: string[]
  packageName?: string | null
  firstDetectedAt?: string | Date | null
  slaDueAt?: string | Date | null
  slaState?: string
  /**
   * RFC-142 backlink — the finding this risk was materialized from. Present
   * only on risks created from a remediation ticket. When the finding is
   * CES-anchored, `controlEvidenceStatusId` deep-links to its test page (#935).
   */
  sourceFinding?: {
    id: string
    title: string
    status: string
    severity: string | null
    controlEvidenceStatusId: string | null
  } | null
  linkedTests?: Array<{
    cesId: string
    displayId: string | null
    title: string
    status: string | null
    lastTestedAt: string | Date | null
    updatedAt: string | Date | null
    dueDate: string | Date | null
    linkSource: string
    rationale: string | null
  }>
  linkedAssets?: Array<{
    assetId: string
    name: string
    assetType: string | null
    criticality: string | null
    sequenceNumber: number | null
  }>
  linkedVendors?: Array<{
    vendorId: string
    name: string
    category: string | null
    riskTier: string | null
    baaStatus: string | null
    sequenceNumber: number | null
  }>
  acceptance?: RiskAcceptanceDTO | null
  canDecideAcceptance?: boolean
  canRequestAcceptance?: boolean
}

function titleCaseLevel(level: string): string {
  return level.replaceAll('_', ' ').toLowerCase().replace(/^./, value => value.toUpperCase())
}

/**
 * #1132 (RFC-214) — deterministic residual suggestion derived from the risk's
 * linked verifying tests and their readiness. Shows the suggested L/I, an Apply
 * action (writes residualLikelihood/Impact — never auto-overwrites), and a
 * "why" breakdown of the contributing links. Hidden when there are no links.
 */
export function ResidualSuggestionCard({
  riskId,
  readOnly,
  currentLikelihood,
  currentImpact,
  onApply,
}: {
  riskId: string
  readOnly?: boolean
  currentLikelihood?: string | null
  currentImpact?: string | null
  onApply: (data: {
    residualLikelihood: string
    residualImpact: string
  }) => void
}) {
  const [showWhy, setShowWhy] = useState(false)
  const { data } = useQuery(
    trpc.riskRegister.suggestResidual.queryOptions({ riskId })
  )
  const suggestion = data?.suggestion
  const contributingLinks = data?.contributingLinks ?? []
  if (!suggestion || contributingLinks.length === 0) return null

  const matchesCurrent =
    currentLikelihood === suggestion.residualLikelihood &&
    currentImpact === suggestion.residualImpact
  const reduces =
    suggestion.likelihoodStepsReduced > 0 || suggestion.impactStepsReduced > 0

  return (
    <div className='border-status-blue-border bg-status-blue-subtle space-y-2 rounded-sm border p-3'>
      <div className='flex items-start gap-2'>
        <ShieldCheck className='text-status-blue-text mt-0.5 h-4 w-4 shrink-0' />
        <div className='min-w-0 flex-1'>
          <p className='text-text-secondary text-[12px] font-semibold'>
            Suggested residual
          </p>
          <p className='text-text-body text-[11px]'>
            {titleCaseLevel(suggestion.residualLikelihood)} likelihood ·{' '}
            {titleCaseLevel(suggestion.residualImpact)} impact{' '}
            <span className='text-text-subtle'>
              ({suggestion.readyCount} of {suggestion.totalCount} linked test
              {suggestion.totalCount === 1 ? '' : 's'} passing)
            </span>
          </p>
          {!reduces && (
            <p className='text-text-subtle mt-1 text-[11px]'>
              Linked tests don’t lower this risk yet: none have passing or
              approved evidence.
            </p>
          )}
        </div>
      </div>
      <div className='flex items-center gap-3 pl-6'>
        {!readOnly && reduces && !matchesCurrent && (
          <Button
            size='sm'
            variant='outline'
            className='border-status-blue-border bg-card h-6 px-2 text-[11px]'
            onClick={() =>
              onApply({
                residualLikelihood: suggestion.residualLikelihood,
                residualImpact: suggestion.residualImpact,
              })
            }
          >
            Apply
          </Button>
        )}
        {matchesCurrent && (
          <span className='text-status-blue-text text-[11px]'>
            Matches current residual
          </span>
        )}
        <button
          type='button'
          className='text-text-subtle text-[11px] underline-offset-2 hover:underline'
          onClick={() => setShowWhy((v) => !v)}
        >
          {showWhy ? 'Hide' : 'Why?'}
        </button>
      </div>
      {showWhy && (
        <ul className='space-y-1 pl-6'>
          {contributingLinks.map((l) => (
            <li
              key={l.cesId}
              className='flex min-w-0 items-center gap-2 text-[11px]'
            >
              <span
                className={l.ready ? 'text-[#1A8A4A]' : 'text-text-placeholder'}
              >
                {l.ready ? '●' : '○'}
              </span>
              <span className='text-text-secondary shrink-0 font-medium'>
                {l.displayId ?? l.title}
              </span>
              {l.displayId && (
                <span className='text-text-subtle truncate'>{l.title}</span>
              )}
              {!l.ready && (
                <span className='text-text-placeholder shrink-0'>
                  · no passing evidence
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function sourceLinkText(provider?: string | null): string {
  if (provider === 'aikido') return 'View on Aikido'
  if (provider === 'snyk') return 'View on Snyk'
  if (provider === 'github') return 'View on GitHub'
  return 'View source'
}

function providerLabel(provider?: string | null): string {
  if (!provider || provider === 'manual') return 'manual'
  const map: Record<string, string> = {
    snyk: 'Snyk',
    aikido: 'Aikido Security',
    github: 'GitHub',
    github_alerts: 'GitHub Alerts',
    aws_security_hub: 'AWS Security Hub',
    azure_defender: 'Azure Defender',
    gcp_scc: 'GCP SCC',
    crowdstrike: 'CrowdStrike',
    wiz: 'Wiz',
    orca: 'Orca',
  }
  return map[provider] ?? provider.charAt(0).toUpperCase() + provider.slice(1)
}

function SyncedFromPill({ provider }: { provider?: string | null }) {
  if (!provider || provider === 'manual') return null
  const label = providerLabel(provider)
  return (
    <span
      className='font-body border-border bg-background text-text-subtle inline-flex h-5 items-center gap-1 rounded-sm border px-2 text-[11px] font-medium'
      title={`Synced from ${label}. Risk text, likelihood, and impact are scanner-owned and read-only here.`}
    >
      Synced · {label}
    </span>
  )
}

const BODY_TEXT_CLASS = 'font-body text-[14px] leading-[1.6] text-text-body'
const MARKDOWN_TEXT_CLASS =
  'font-body max-w-none text-[14px] leading-[1.6] text-text-body [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a]:text-cobalt-strong [&_a]:underline-offset-2 [&_a:hover]:underline [&_code]:rounded-sm [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px] [&_h1]:font-heading [&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-[17px] [&_h1]:font-semibold [&_h1]:leading-tight [&_h2]:font-heading [&_h2]:mb-1.5 [&_h2]:mt-4 [&_h2]:text-[15px] [&_h2]:font-semibold [&_h2]:leading-tight [&_h3]:font-heading [&_h3]:mb-1.5 [&_h3]:mt-3 [&_h3]:text-[14px] [&_h3]:font-semibold [&_li]:my-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-sm [&_pre]:bg-background [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[12px] [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1.5 [&_th]:border [&_th]:border-border [&_th]:bg-background [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5'
const SURFACE_CLASS = 'rounded-sm bg-card'
export function RiskScoreBadge({
  score,
  rating,
  label = 'Risk',
  muted = false,
}: {
  score: number | null | undefined
  rating: string
  label?: string
  muted?: boolean
}) {
  if (score == null || score === 0) return null
  return (
    <span
      className={`font-body inline-flex h-6 items-center gap-1.5 rounded-sm border px-2 text-[11px] font-medium ${muted ? 'border-border bg-status-gray-bg text-status-gray' : riskRatingBadgeClass(rating)}`}
      title='Likelihood × Impact, normalized to 100'
    >
      <span className='text-[10px] tracking-[0.06em] uppercase opacity-70'>
        {label}
      </span>
      <span className='text-[13px] font-semibold tabular-nums'>{score}</span>
    </span>
  )
}

// #996: derived remediation-SLA chip. Renders only for open vulnerabilities
// that carry a deadline (RESOLVED/NONE convey nothing the row doesn't already).
const SLA_CHIP: Record<string, { label: string; color: StatusColor }> = {
  WITHIN_SLA: { label: 'Within SLA', color: 'green' },
  DUE_SOON: { label: 'Due soon', color: 'amber' },
  BREACHED: { label: 'SLA breached', color: 'red' },
}

function SlaChip({
  state,
  dueAt,
}: {
  state?: string
  dueAt?: string | Date | null
}) {
  const config = state ? SLA_CHIP[state] : undefined
  if (!config) return null
  return (
    <span
      className={`font-body inline-flex h-6 items-center rounded-sm px-2 text-[11px] font-medium whitespace-nowrap ${statusBadgeClasses[config.color]}`}
      title={dueAt ? `Remediation due ${formatDate(dueAt)}` : undefined}
    >
      {config.label}
    </span>
  )
}

// #996: one label/value row inside the Vulnerability surface (dense,
// label-left / value-right — matches the operational rhythm of the page).
function VulnFactRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className='flex items-start justify-between gap-4 px-4 py-2.5'>
      <span className='font-body text-text-subtle shrink-0 text-[11px] tracking-[0.06em] uppercase'>
        {label}
      </span>
      <div className='font-body text-text-secondary min-w-0 text-right text-[12px]'>
        {children}
      </div>
    </div>
  )
}

/** Risk text is written line by line ("**Resource**: …" then "**Remediation**: …"),
 *  so a single newline is a line break, not a space. Code fences stay verbatim. */
export function withLineBreaks(markdown: string): string {
  return markdown
    .split(/(```[\s\S]*?```)/g)
    .map((part, index) =>
      index % 2 ? part : part.replace(/([^\n])\n(?!\n)/g, '$1  \n')
    )
    .join('')
}

export function MarkdownDetailContent({ content, collapsible = false }: { content?: string | null; collapsible?: boolean }) {
  const descriptionRef = useRef<HTMLDivElement>(null)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [descriptionOverflows, setDescriptionOverflows] = useState(false)
  useLayoutEffect(() => {
    if (!collapsible) return
    const content = descriptionRef.current
    if (!content) return
    setDescriptionExpanded(false)
    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(content).lineHeight)
      setDescriptionOverflows(content.scrollHeight > lineHeight * 3 + 1)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    return () => observer.disconnect()
  }, [collapsible, content])

  const trimmed = content?.trim()

  if (!trimmed) {
    return <p className={BODY_TEXT_CLASS}>—</p>
  }

  const markdown = (
    <div className='overflow-x-auto'>
      <Streamdown key={trimmed} mode='static' className={MARKDOWN_TEXT_CLASS} skipHtml>
        {withLineBreaks(trimmed)}
      </Streamdown>
    </div>
  )

  if (!collapsible) return markdown

  return (
    <ShowMoreClamp
      className={BODY_TEXT_CLASS}
      showFade={!descriptionExpanded && descriptionOverflows}
      showToggle={descriptionOverflows}
      expanded={descriptionExpanded}
      onToggle={() => setDescriptionExpanded(value => !value)}
      contentClassName={descriptionExpanded ? undefined : 'max-h-[4.8em] overflow-hidden'}
    >
      <div ref={descriptionRef} className='[&>div]:py-0'>{markdown}</div>
    </ShowMoreClamp>
  )
}

export default function RiskDetailPanel({
  item,
  onUpdate,
  onMetadataSaved,
  readOnly = false,
  acceptanceInAssessmentPanel = false,
  reviewingResidual = false,
  prioritizeResidualAssessment = false,
  viewStage,
  tab,
  onTabChange,
  assessmentPanel,
  assessmentHistory,
  openAssessmentActions,
  pendingAssessment,
  assessmentPreview,
  inProgressAssessment,
  closedAssessment,
  closedActions,
  menuActions,
}: RiskDetailPanelProps) {
  const queryClient = useQueryClient()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const canManageValidity = !readOnly && item.canManageValidity
  readOnly = readOnly || Boolean(item.invalidatedAt) || item.status === 'CLOSED'
  const actualStage = item.status === 'ASSESSING' ? 'OPEN' : item.status
  const viewingPreviousStage = Boolean(viewStage && viewStage !== actualStage)
  const showOpen = !item.invalidatedAt && (viewStage ?? actualStage) === 'OPEN'
  const [validityDialogOpen, setValidityDialogOpen] = useState(false)
  const [validityReason, setValidityReason] = useState('')
  const validityMutation = useMutation(trpc.riskRegister.setValidity.mutationOptions())
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)
  const metadataMutation = useMutation(trpc.riskRegister.update.mutationOptions())
  const canEditMetadata = !readOnly && item.canAssessRisk && ['OPEN', 'PENDING_REVIEW', 'IN_PROGRESS'].includes(item.status) && !item.aiAssessmentRunning && !viewingPreviousStage
  const rolesQuery = useQuery(trpc.complianceRole.list.queryOptions(undefined, { enabled: Boolean(canEditMetadata && item.canAssignRiskOwner) }))
  const saveMetadata = async (data: { category?: string; ownerId?: string | null; ownerLabel?: string; targetDate?: Date | null; reviewDate?: Date | null }) => {
    setUpdateError(null)
    try {
      const updated = await metadataMutation.mutateAsync({ id: item.id, data })
      onMetadataSaved?.(updated)
      onChanged()
    } catch (error) { setUpdateError(error instanceof Error ? error.message : 'Could not update risk metadata') }
  }
  const [internalTab, setInternalTab] = useState('details')
  const activeTab = (['scope', 'source'].includes(tab ?? internalTab) || (item.status === 'CLOSED' && ['tests', 'tasks'].includes(tab ?? internalTab))) ? 'details' : tab ?? internalTab
  // Tab badge: open (non-terminal) tasks linked to this risk. `task.list`
  // excludes RESOLVED/DISMISSED by default, so `total` is the unresolved count.
  const openTasksQuery = useQuery(
    trpc.task.list.queryOptions({ riskItemId: item.id, limit: 1 })
  )
  const openTaskCount = openTasksQuery.data?.total ?? 0
  const handleTabChange = (next: string) => {
    if (onTabChange) onTabChange(next)
    else setInternalTab(next)
  }

  const linkedTests = item.linkedTests ?? []
  const linkedAssets = item.linkedAssets ?? []
  const linkedVendors = item.linkedVendors ?? []
  const acceptance = item.acceptance ?? null
  const isSynced = item.provider !== 'manual'
  const hasActiveAcceptance = !!acceptance && acceptance.status === 'ACTIVE' && !!acceptance.approvedAt && new Date(acceptance.expiresAt).getTime() > Date.now()
  const hasExpiredAcceptance = !!acceptance && (acceptance.status === 'EXPIRED' || (acceptance.status === 'ACTIVE' && !hasActiveAcceptance))
  const hasVulnerabilityDetails = Boolean(
    item.cveIds?.length ||
    item.advisoryIds?.length ||
    item.packageName ||
    item.firstDetectedAt ||
    item.slaDueAt
  )
  const hasSourceDetails = Boolean(
    item.externalUrl ||
    item.sourceFinding ||
    (item.syncWarnings && item.syncWarnings.length > 0)
  )

  const refreshActivity = () => {
    // Procedure-level prefix — invalidates every listActivity variant
    // regardless of limit/cursor so Load-More pagination doesn't go stale.
    queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.listActivity.queryKey(),
    })
  }

  const onChanged = () => {
    onUpdate()
    refreshActivity()
    // Linking/unlinking controls or editing residual changes the #1132
    // residual suggestion — refresh it (procedure-level prefix).
    queryClient.invalidateQueries({
      queryKey: trpc.riskRegister.suggestResidual.queryKey(),
    })
  }

  const showResidualFirst = !item.invalidatedAt && (item.status === 'CLOSED' || (prioritizeResidualAssessment && item.status === 'IN_PROGRESS'))
  const description = <div className='[&>div]:py-0'><MarkdownDetailContent content={item.riskStatement} collapsible /></div>
  const sourceSection = (
    <div className='space-y-6'>
      {hasVulnerabilityDetails && (
        <DetailSection title='Vulnerability'>
          <div
            className={`${SURFACE_CLASS} divide-border border-border divide-y border`}
          >
            {(item.cveIds?.length || item.advisoryIds?.length) && (
              <VulnFactRow label='Identifiers'>
                <div className='flex flex-wrap justify-end gap-1.5'>
                  {item.cveIds?.map((cve) => (
                    <a
                      key={cve}
                      href={`https://nvd.nist.gov/vuln/detail/${cve}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='border-border-divider bg-surface-subtle text-cobalt-strong inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium tabular-nums hover:underline'
                    >
                      {cve}
                      <ExternalLink className='h-2.5 w-2.5' />
                    </a>
                  ))}
                  {item.advisoryIds?.map((ghsa) => (
                    <a
                      key={ghsa}
                      href={`https://github.com/advisories/${ghsa}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='border-border-divider bg-surface-subtle text-cobalt-strong inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium hover:underline'
                    >
                      {ghsa}
                      <ExternalLink className='h-2.5 w-2.5' />
                    </a>
                  ))}
                </div>
              </VulnFactRow>
            )}
            {item.packageName && (
              <VulnFactRow label='Affected package'>
                <span className='text-text-body font-mono text-[11px]'>
                  {item.packageName}
                </span>
              </VulnFactRow>
            )}
            {item.firstDetectedAt && (
              <VulnFactRow label='First detected'>
                {formatDate(item.firstDetectedAt)}
              </VulnFactRow>
            )}
            {item.slaDueAt && (
              <VulnFactRow label='Remediation SLA'>
                <span className='inline-flex items-center gap-2'>
                  <SlaChip state={item.slaState} dueAt={item.slaDueAt} />
                  <span className='text-text-subtle'>
                    due {formatDate(item.slaDueAt)}
                  </span>
                </span>
              </VulnFactRow>
            )}
          </div>
        </DetailSection>
      )}

      {hasSourceDetails && (
        <DetailSection title='Source details'>
          <div className={`${SURFACE_CLASS} space-y-4`}>
            {item.sourceFinding && (
              <div>
                <span className='font-body text-text-placeholder text-[11px] tracking-[0.06em] uppercase'>
                  Detected from finding
                </span>
                <div className='mt-0.5'>
                  {item.sourceFinding.controlEvidenceStatusId &&
                  workspaceId ? (
                    <Link
                      to='/workspaces/$workspaceId/tests/$testId'
                      params={{
                        workspaceId,
                        testId:
                          item.sourceFinding.controlEvidenceStatusId,
                      }}
                      className='font-body text-cobalt-strong text-[12px] hover:underline'
                    >
                      {item.sourceFinding.title}
                    </Link>
                  ) : (
                    <span className='font-body text-text-secondary text-[12px]'>
                      {item.sourceFinding.title}
                    </span>
                  )}
                </div>
              </div>
            )}

            {item.externalUrl && !readOnly && (
              <div>
                <a
                  href={item.externalUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-body text-cobalt-strong inline-flex items-center gap-1 text-[12px] hover:underline'
                >
                  {sourceLinkText(item.provider)}{' '}
                  <ExternalLink className='h-3 w-3' />
                </a>
              </div>
            )}

            {item.syncWarnings && item.syncWarnings.length > 0 && (
              <div className='border-status-orange/30 bg-status-orange-bg rounded-sm border px-4 py-3'>
                <div className='mb-1 flex items-center gap-1.5'>
                  <AlertCircle className='text-status-orange h-3.5 w-3.5' />
                  <span className='font-body text-status-orange-strong text-[12px] font-semibold'>
                    Sync Warnings
                  </span>
                </div>
                <ul className='font-body text-status-orange-strong space-y-0.5 text-[11px]'>
                  {item.syncWarnings?.map(
                    (warning: string, index: number) => (
                      <li key={index}>• {warning}</li>
                    )
                  )}
                </ul>
              </div>
            )}
          </div>
        </DetailSection>
      )}


    </div>
  )

  const scoreOverview = (
    <section className={item.status === 'CLOSED' && !viewingPreviousStage ? 'flex flex-col px-6 [&>div:last-child]:mt-auto [&>div:last-child]:pt-7' : 'px-6'}>
      <h2 className={DETAIL_SECTION_TITLE_CLASS}>Risk score</h2>
      <div className={showResidualFirst ? 'mt-6 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4' : 'mt-6 flex items-center gap-4'}>
        <div className={showResidualFirst ? 'min-w-0 space-y-2 text-left' : 'flex items-center gap-4'}>
          <p className={`${item.status === 'CLOSED' ? 'text-[56px] leading-none' : 'text-7xl'} font-light tracking-tighter ${riskRatingSignal(item.riskRating).className}`}>{item.riskScore ?? (item.invalidatedAt ? '-' : '—')}</p>
          <p className='text-xs text-muted-foreground'>Inherent risk</p>
        </div>
        {showResidualFirst && <>
          <div className={`flex h-[1em] items-center self-start ${item.status === 'CLOSED' ? 'text-[56px]' : 'text-7xl'}`}><ArrowRight className='size-5 text-muted-foreground' aria-label='Risk score change' /></div>
          <div className='min-w-0 space-y-2 text-right'>
            <p className={`${item.status === 'CLOSED' ? 'text-[56px] leading-none' : 'text-7xl'} font-light tracking-tighter ${riskRatingSignal(item.residualRisk ?? '').className}`}>{item.residualScore ?? '-'}</p>
            <p className='text-xs text-muted-foreground'>Residual risk</p>
          </div>
        </>}
      </div>
      <RiskMatrixCard counts={[]} title='' scoringVersion={item.scoringVersion ?? 2} marker={{ likelihood: item.likelihood, impact: item.impact }}
        treatmentLabel={item.treatment ? titleCaseLevel(item.treatment) : undefined}
        residualMarker={{ likelihood: item.residualLikelihood ?? null, impact: item.residualImpact ?? null }} reviewed={item.status === 'CLOSED'} />
    </section>
  )

  return (
    <DetailPanelLayout>
      {acceptDialogOpen && !hasActiveAcceptance && (
        <AcceptRiskDialog
          open={acceptDialogOpen}
          onOpenChange={setAcceptDialogOpen}
          riskId={item.id}
          initialAcceptance={acceptance}
          onAccepted={() => {
            setAcceptDialogOpen(false)
            onChanged()
          }}
        />
      )}
      <DetailPanelMain>
        <DetailPanelTitle
          code={item.displayId}
          title={item.title}
        >
          {(item.slaState ||
            (item.provider && item.provider !== 'manual') ||
            hasActiveAcceptance) && (
            <div className='mt-2 flex flex-wrap items-center gap-x-2 gap-y-1'>
              <SlaChip state={item.slaState} dueAt={item.slaDueAt} />
              <SyncedFromPill provider={item.provider} />
              {hasActiveAcceptance && (
                <AcceptedHeaderPill expiresAt={acceptance!.expiresAt} />
              )}
            </div>
          )}
        </DetailPanelTitle>
        <section aria-label='Risk metadata'><DetailStatusGrid className='mt-5 !max-w-none grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4'>
          <div aria-label='Risk status'><DetailStatusItem label='Status'>
            <span className='flex h-9 items-center'><StatusBadge status={item.status} /></span>
          </DetailStatusItem></div>
          <DetailStatusItem label='Owner'>
            {canEditMetadata && item.canAssignRiskOwner ? <RoleSelect value={item.ownerId ?? null}
              roles={rolesQuery.data ?? []} disabled={metadataMutation.isPending || rolesQuery.isPending || rolesQuery.isError}
              ariaLabel='Risk owner' placeholder='Assign owner' fullWidth className='h-9'
              onValueChange={ownerId => void saveMetadata({ ownerId, ownerLabel: rolesQuery.data?.find(role => role.id === ownerId)?.name ?? 'Unassigned' })} />
              : <span className='flex h-9 items-center text-sm'>{item.ownerRoleName || item.ownerLabel || 'Unassigned'}</span>}
          </DetailStatusItem>
          <DetailStatusItem label={canEditMetadata ? <Label htmlFor='risk-target-date' className='text-[11px]'>Target date</Label> : 'Target date'}>
            {canEditMetadata ? <DatePicker id='risk-target-date' clearLabel='Clear target date' value={item.targetDate ? new Date(item.targetDate) : undefined}
              disabled={metadataMutation.isPending} className='h-9 w-full' placeholder='Set date'
              onChange={date => void saveMetadata({ targetDate: date ?? null })} />
              : <span className='flex h-9 items-center text-sm'>{item.targetDate ? formatDate(item.targetDate) : 'Not set'}</span>}
          </DetailStatusItem>
          <DetailStatusItem label={canEditMetadata ? <Label htmlFor='risk-review-date' className='text-[11px]'>Review date</Label> : 'Review date'}>
            {canEditMetadata ? <DatePicker id='risk-review-date' clearLabel='Clear review date' value={item.reviewDate ? new Date(item.reviewDate) : undefined}
              disabled={metadataMutation.isPending} className='h-9 w-full' placeholder='Set date'
              onChange={date => void saveMetadata({ reviewDate: date ?? null })} />
              : <span className='flex h-9 items-center text-sm'>{item.reviewDate ? formatDate(item.reviewDate) : 'Not set'}</span>}
          </DetailStatusItem>
        </DetailStatusGrid></section>

        {item.invalidatedAt && (
          <DetailSection title='Dismissed risk'>
            <p className='text-sm text-muted-foreground'>Excluded from the current register, risk matrix and mitigation totals. History is retained.</p>
            <p className='mt-2 text-sm'>{item.invalidationReason}</p>
          </DetailSection>
        )}
        {(menuActions || canManageValidity) && (
          <PageHeaderActions>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' size='smIcon' aria-label='More risk actions'>
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {menuActions}
                {canManageValidity && (
                  <DropdownMenuItem disabled={validityMutation.isPending} onSelect={() => { setValidityReason(''); setUpdateError(null); setValidityDialogOpen(true) }}>
                    {item.invalidatedAt ? <RotateCcw className='size-4' /> : <CircleSlash className='size-4' />}
                    {item.invalidatedAt ? 'Restore risk…' : 'Dismiss risk…'}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </PageHeaderActions>
        )}
        <Dialog open={validityDialogOpen} onOpenChange={setValidityDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{item.invalidatedAt ? 'Restore risk?' : 'Dismiss this risk?'}</DialogTitle>
              <DialogDescription>{item.invalidatedAt ? 'Explain the evidence that makes this a valid risk again.' : 'Use this when the risk was incorrectly identified or does not apply. Its history will be retained, and you can restore it later. For a risk that has been treated, use Close risk.'}</DialogDescription>
            </DialogHeader>
            <div className='px-6'>
              <Textarea aria-label='Validity decision reason' maxLength={2000} value={validityReason} onChange={(event) => setValidityReason(event.target.value)} />
              {updateError && <p role='alert' className='mt-2 text-sm text-status-red'>{updateError}</p>}
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={() => setValidityDialogOpen(false)}>Cancel</Button>
              <Button disabled={!validityReason.trim() || validityMutation.isPending} onClick={async () => {
                try {
                  const result = await validityMutation.mutateAsync({ id: item.id, invalidated: !item.invalidatedAt, reason: validityReason })
                  if (result.findingsRequiringReview) toast.warning(`${result.findingsRequiringReview} linked finding(s) have evidence or work in progress and still need separate review.`)
                  setValidityDialogOpen(false)
                  await queryClient.invalidateQueries({ queryKey: trpc.riskRegister.pathKey() })
                  await queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.listOpenFindingsByCES.queryKey() })
                  onChanged()
                } catch (error) { setUpdateError(error instanceof Error ? error.message : 'Validity update failed') }
              }}>{validityMutation.isPending ? (item.invalidatedAt ? 'Restoring...' : 'Dismissing...') : (item.invalidatedAt ? 'Restore risk' : 'Dismiss risk')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {!item.invalidatedAt && (typeof assessmentPanel === 'function' ? assessmentPanel(description) : assessmentPanel)}
        {item.status !== 'CLOSED' && !showOpen && !viewStage && (item.invalidatedAt || typeof assessmentPanel !== 'function') && <div className='mt-5'>{description}</div>}

        {updateError && (
          <p role='alert' className='font-body text-status-red mt-6 text-[11px]'>{updateError}</p>
        )}

        <div className={item.status === 'CLOSED' && !viewingPreviousStage && typeof closedAssessment === 'function' ? 'mt-6' : `mt-6 grid items-stretch gap-6 ${item.status === 'CLOSED' ? 'lg:grid-cols-[minmax(0,1fr)_300px]' : 'lg:grid-cols-[minmax(0,1fr)_340px]'}`}>
          <div className='min-w-0 space-y-5'>
            {showOpen && <section aria-label='Risk background' className='space-y-6'>
              <DetailSection title='Risk context'>
                <DetailStatusGrid className='!max-w-none grid-cols-2 gap-4 sm:grid-cols-4'>
                  <DetailStatusItem label='Type'><span title='Type determines the risk ID prefix and is fixed after creation.' className='flex h-9 items-center text-sm'>{item.riskType ? titleCaseLevel(item.riskType) : 'Not set'}</span></DetailStatusItem>
                  <DetailStatusItem label={<Label htmlFor='risk-category' className='text-[11px]'>Category</Label>}>
                    {canEditMetadata && !isSynced ? <Select value={item.category || undefined} disabled={metadataMutation.isPending}
                      onValueChange={category => { if (category !== item.category) void saveMetadata({ category }) }}>
                      <SelectTrigger id='risk-category' aria-label='Category' fullWidth className='h-9'><SelectValue placeholder='Select category' /></SelectTrigger>
                      <SelectContent>
                        {item.category && !RISK_CATEGORY_OPTIONS.includes(item.category) && <SelectItem value={item.category}>{item.category}</SelectItem>}
                        {RISK_CATEGORY_OPTIONS.map(category => <SelectItem key={category} value={category}>{category}</SelectItem>)}
                      </SelectContent>
                    </Select> : <span className='flex h-9 items-center text-sm'>{item.category || 'Not set'}</span>}
                  </DetailStatusItem>
                  <DetailStatusItem label='Origin'><span className='flex h-9 items-center text-sm'>{item.provider === 'manual' ? 'Manual' : item.provider ? providerLabel(item.provider) : 'Not set'}</span></DetailStatusItem>
                  <DetailStatusItem label='Created'><span className='flex h-9 items-center text-sm'>{item.createdAt ? formatDate(item.createdAt) : 'Not set'}</span></DetailStatusItem>
                </DetailStatusGrid>
              </DetailSection>
              <DetailSection title='Description'>
                <MarkdownDetailContent content={item.riskStatement} collapsible />
              </DetailSection>
              <AffectedAssetsSection riskId={item.id} assets={linkedAssets} onChanged={onChanged}
                readOnly={readOnly || viewingPreviousStage} allowReadOnlyLinks />
              <RelatedVendorsSection riskId={item.id} vendors={linkedVendors} onChanged={onChanged}
                readOnly={readOnly || viewingPreviousStage} allowReadOnlyLinks />
              {sourceSection}
              {!viewingPreviousStage && <fieldset disabled={metadataMutation.isPending}>{openAssessmentActions}</fieldset>}
            </section>}

            <fieldset disabled={metadataMutation.isPending} hidden={showOpen}>
            {viewingPreviousStage && <div className='space-y-4'>
              {viewStage === 'PENDING_REVIEW' && assessmentPreview}
              {viewStage === 'IN_PROGRESS' && <>
                {acceptance && <AcceptanceCard riskId={item.id} acceptance={acceptance} readOnly canDecide={false} canRequestApproval={false} onRequestApproval={() => {}} onChanged={onChanged} />}
                {inProgressAssessment ?? assessmentPreview}
              </>}
            </div>}
            <div hidden={viewingPreviousStage} className='space-y-5'>
            {item.status === 'IN_PROGRESS' && !reviewingResidual && hasActiveAcceptance && acceptance && <AcceptanceCard riskId={item.id} acceptance={acceptance}
              readOnly={readOnly} canDecide={item.canDecideAcceptance ?? false} canRequestApproval={item.canRequestAcceptance ?? false}
              onRequestApproval={() => setAcceptDialogOpen(true)} onChanged={onChanged} />}
            {item.status === 'IN_PROGRESS' && !acceptanceInAssessmentPanel && hasExpiredAcceptance && <div className='space-y-2'>
              <ExpiredAcceptanceBanner expiresAt={acceptance!.expiresAt} />
              {!readOnly && item.canRequestAcceptance && <Button variant='outline' size='sm' onClick={() => setAcceptDialogOpen(true)}>Request new approval</Button>}
            </div>}
            {item.status === 'CLOSED' ? (typeof closedAssessment === 'function' ? closedAssessment(scoreOverview) : closedAssessment ?? assessmentPreview)
              : item.status === 'IN_PROGRESS' ? inProgressAssessment ?? assessmentPreview
              : item.status === 'PENDING_REVIEW' && !readOnly ? pendingAssessment ?? assessmentPreview
              : !showOpen ? assessmentPreview : null}
            {item.status === 'CLOSED' && <>
              <LinkedTestsSection riskId={item.id} tests={linkedTests} onChanged={onChanged} readOnly allowReadOnlyLinks />
              {!viewingPreviousStage && <LinkedTasksSection riskId={item.id} readOnly />}
              <AffectedAssetsSection riskId={item.id} assets={linkedAssets} onChanged={onChanged} readOnly allowReadOnlyLinks />
              <RelatedVendorsSection riskId={item.id} vendors={linkedVendors} onChanged={onChanged} readOnly allowReadOnlyLinks />
              {sourceSection}
              {closedActions}
            </>}
            </div>
            </fieldset>
          </div>
          {!(item.status === 'CLOSED' && !viewingPreviousStage && typeof closedAssessment === 'function') && scoreOverview}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className='mt-6 w-full'
        >
          <TabsList className='w-full'>
            <TabsTrigger value='details'>Details</TabsTrigger>
            {item.status !== 'CLOSED' && <TabsTrigger value='tests'>Tests</TabsTrigger>}
            {item.status !== 'CLOSED' && <TabsTrigger value='tasks'>
              Tasks{openTaskCount > 0 ? ` (${openTaskCount})` : ''}
            </TabsTrigger>}
            {assessmentHistory && <TabsTrigger value='history'>Assessments</TabsTrigger>}
          </TabsList>

          {assessmentHistory && <TabsContent value='history' className='mt-4'>
            {assessmentHistory}
          </TabsContent>}

          <TabsContent value='details' className='mt-4'>
            <div className='space-y-6'>
              {isSynced && (
                <p className='font-body text-text-placeholder text-[11px] italic'>
                  Synced from {item.provider}. Content is read-only. Owner,
                  dates, status, links, and acceptance can be edited.
                </p>
              )}

              <RiskActivitySection riskId={item.id} />
            </div>
          </TabsContent>

          {item.status !== 'CLOSED' && <TabsContent value='tests' className='mt-6 space-y-6'>
            <LinkedTestsSection
              riskId={item.id}
              tests={linkedTests}
              onChanged={onChanged}
              readOnly={readOnly}
            />
          </TabsContent>}

          {item.status !== 'CLOSED' && <TabsContent value='tasks' className='mt-6 space-y-6'>
            <LinkedTasksSection riskId={item.id} readOnly={readOnly} />
          </TabsContent>}

        </Tabs>
      </DetailPanelMain>
    </DetailPanelLayout>
  )
}
